/**
 * Format included trip distance for Booking Summary (KAN-199).
 * Handles unlimited / zero policies gracefully.
 *
 * Conventions:
 * - per-day > 0 → "Included distance · 1000 km (200 km/day × 5 days)"
 * - per-day === 0 with overage fee → no included allowance
 * - per-day === 0 and overage fee === 0 → unlimited
 * - null / non-finite → omit
 */
export function formatIncludedDistance({
  includedKmPerDay,
  includedKmTotal,
  days,
  overageFeePerKm,
} = {}) {
  const perDay = Number(includedKmPerDay);
  const dayCount = Math.max(0, Number(days) || 0);
  const overage = overageFeePerKm != null ? Number(overageFeePerKm) : null;
  const total = includedKmTotal != null
    ? Number(includedKmTotal)
    : (Number.isFinite(perDay) ? perDay * dayCount : NaN);

  if (!Number.isFinite(perDay) && !Number.isFinite(total)) return null;

  if (perDay === 0 || total === 0) {
    const unlimited = overage === 0;
    if (unlimited) {
      return {
        label: "Included distance",
        primary: "Unlimited",
        secondary: null,
        unlimited: true,
      };
    }
    return {
      label: "Included distance",
      primary: "None",
      secondary: "overage charged per km",
      unlimited: false,
    };
  }

  const safeTotal = Number.isFinite(total) ? total : perDay * dayCount;
  const secondary = Number.isFinite(perDay) && dayCount > 0
    ? `${perDay} km/day × ${dayCount} day${dayCount === 1 ? "" : "s"}`
    : null;

  return {
    label: "Included distance",
    primary: `${safeTotal} km`,
    secondary,
    unlimited: false,
  };
}

export function formatIncludedDistanceLine(opts) {
  const formatted = formatIncludedDistance(opts);
  if (!formatted) return null;
  if (formatted.unlimited) {
    return `${formatted.label} · Unlimited`;
  }
  if (formatted.secondary) {
    return `${formatted.label} · ${formatted.primary} (${formatted.secondary})`;
  }
  return `${formatted.label} · ${formatted.primary}`;
}
